import { useCallback, useEffect } from 'react';

import { useRouter } from 'next/router';

import { useRaptorEnvironment } from '~/features/raptor/hooks/useRaptorEnvironment';
import { useGaCookie } from '~/features/tracking/hooks/useGaCookie';
import { GtmGeneric } from '~/features/tracking/model';
import { pushToDataLayer } from '~/features/tracking/utils/pushToDataLayer';
import { useCookieConsent } from '~/shared/components/CookieConsent/hooks/useCookieConsent';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useMarket } from '~/shared/utils/market/hooks/useMarket';
import { isProduction, isSSR } from '~/shared/utils/platform/utils/platform';
import { usePage } from '~/templates/pages/hooks/usePage';

import { useBasket } from '../../basket/hooks/useBasket';

let isTracked = false;
let originalLocation: string;

export const useGenericTracking = () => {
	const page = usePage();
	const router = useRouter();
	const { data: frame } = useFrame();
	const { market, culture } = useMarket();
	const { allowStatistics } = useCookieConsent();
	const { data: basket } = useBasket();
	const { cookieId, reaId, setReaId, sessionId } = useRaptorEnvironment();
	const gaCookie = useGaCookie();

	const track = useCallback(
		(pagePath: string) => {
			pushToDataLayer<GtmGeneric>({
				countrySite: `${culture}-${market}`,
				bu: 'rosendahl',
				environment: isProduction && frame.environment === 'production' ? 'live' : 'dev',
				solution: 'webshop',
				pageType: page.type,
				login: 'false', // TODO: implement tracking of logged in state
				clientID: gaCookie?.substr(6) || '',
				raptorCookieID: cookieId || '',
				anonymousID: basket?.id?.toString() || '',
				originalLocation,
				pagePath,
			});
		},
		[culture, market, frame.environment, page.type, gaCookie, cookieId, basket?.id],
	);

	useEffect(() => {
		if (isSSR) {
			return;
		}

		router.events.on('routeChangeComplete', track);

		return () => {
			router.events.off('routeChangeComplete', track);
		};
	}, [router.events, track]);

	useEffect(() => {
		if (isSSR || isTracked || !sessionId) {
			// We do not want to track the initial data layer twice,
			// nor do we want to track before anonymousID cookie is set.
			return;
		}

		originalLocation = window.location.href;
		track(router.asPath);
		isTracked = true;
	}, [sessionId, router.asPath, track]);

	useEffect(() => {
		if (isSSR || !allowStatistics) {
			return;
		}

		const newReaId = router.query.reaId;

		if (newReaId && typeof newReaId === 'string' && newReaId !== reaId) {
			setReaId(newReaId);
		}
	}, [router.query, allowStatistics, reaId, setReaId]);
};
